import { useState } from 'react';
import { Settings2, Sparkles, ClipboardCheck, PlayCircle } from 'lucide-react';

const steps = [
  {
    icon: Settings2,
    number: '01',
    title: 'Configure Parameters',
    description:
      "Select NCERT, grade, subject, topic, question type, and Bloom's taxonomy level.",
  },
  {
    icon: Sparkles,
    number: '02',
    title: 'AI Generates Questions',
    description:
      'Our AI engine creates pedagogically accurate questions with proper distractors and explanations.',
  },
  {
    icon: ClipboardCheck,
    number: '03',
    title: 'Review & Assemble',
    description:
      'Edit questions inline, bundle them into timed assessments, assign points, and export.',
  },
];

export default function HowItWorks() {
  const [demoLoaded, setDemoLoaded] = useState(false);

  return (
    <section id="how-it-works" className="py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-teal-100/80 text-teal-700 rounded-full text-sm font-medium mb-5">
            <PlayCircle className="w-4 h-4" />
            Platform Guide
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            See AI question generation in action
          </h2>
          <p className="text-lg text-slate-500 mt-4">
            Watch how Xed21 turns your NCERT assessment settings into
            classroom-ready questions, then lets you review and assemble them.
          </p>
        </div>

        <div className="relative mx-auto max-w-5xl mb-20">
          <div className="absolute -inset-4 bg-gradient-to-r from-teal-100 via-emerald-50 to-amber-50 rounded-[2rem] blur-2xl opacity-80" />
          <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-200/70">
            <div className="relative aspect-[1024/640]">
              {demoLoaded ? (
                <iframe
                  src="https://guideless.ai/share/ai-assessment-generation-and-export-in-xed21-ozy0_U4kBJ5x/embed"
                  title="AI Assessment Generation and Export in Xed21"
                  frameBorder="0"
                  loading="lazy"
                  allowFullScreen
                  className="absolute inset-0 h-full w-full"
                  style={{ colorScheme: 'light' }}
                />
              ) : (
                <button
                  type="button"
                  aria-label="Play Xed21 product demo"
                  onClick={() => setDemoLoaded(true)}
                  className="absolute inset-0 flex h-full w-full items-center justify-center overflow-hidden bg-slate-950 text-left"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-teal-950 to-slate-900" />
                  <div className="absolute inset-x-10 top-8 h-10 rounded-t-xl border border-white/10 bg-white/10" />
                  <div className="relative w-[78%] rounded-xl border border-white/15 bg-white p-5 shadow-2xl">
                    <div className="mb-4 flex items-center justify-between border-b border-slate-100 pb-4">
                      <div>
                        <div className="h-3 w-32 rounded bg-teal-700" />
                        <div className="mt-2 h-2 w-48 rounded bg-slate-200" />
                      </div>
                      <div className="h-8 w-28 rounded-lg bg-teal-700" />
                    </div>
                    <div className="grid gap-4 md:grid-cols-[1.1fr_0.9fr]">
                      <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <div className="h-3 w-20 rounded bg-slate-300" />
                        <div className="h-9 rounded-lg bg-white" />
                        <div className="h-9 rounded-lg bg-white" />
                        <div className="h-9 rounded-lg bg-white" />
                      </div>
                      <div className="space-y-3 rounded-xl border border-slate-200 p-4">
                        <div className="h-3 w-24 rounded bg-emerald-600" />
                        <div className="h-4 rounded bg-slate-200" />
                        <div className="h-4 w-5/6 rounded bg-slate-200" />
                        <div className="space-y-2 pt-2">
                          <div className="h-7 rounded-lg bg-emerald-50" />
                          <div className="h-7 rounded-lg bg-slate-100" />
                          <div className="h-7 rounded-lg bg-slate-100" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <span className="absolute flex h-16 w-16 items-center justify-center rounded-full bg-white text-teal-700 shadow-2xl transition-transform duration-200 hover:scale-105">
                    <PlayCircle className="h-9 w-9" />
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="text-center max-w-2xl mx-auto mb-16">
          <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            Three Simple Steps
          </h3>
          <p className="text-lg text-slate-500 mt-4">
            From NCERT topic selection to classroom-ready questions in minutes.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-12 md:gap-8 relative">
          <div className="hidden md:block absolute top-[52px] left-[20%] right-[20%] h-px bg-gradient-to-r from-transparent via-teal-200 to-transparent" />

          {steps.map((step) => (
            <div key={step.number} className="relative text-center">
              <div className="inline-flex items-center justify-center w-[72px] h-[72px] bg-teal-50 rounded-2xl mb-6 relative z-10 border border-teal-100">
                <step.icon className="w-8 h-8 text-teal-600" />
              </div>
              <div className="text-xs font-bold text-teal-700 tracking-widest uppercase mb-2">
                Step {step.number}
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                {step.title}
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed max-w-xs mx-auto">
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
